import { useCallback, useEffect, useMemo, useState } from "react";
import {
  resolveDetailWindow,
  mergeTelemetryData,
  type TimeWindow,
} from "@/utils/telemetryDetailWindow";
import type { TimelineRange } from "@/utils/sharedTimeline";
import {
  TELEMETRY_CHART_MAX_POINTS,
  useTelemetryData,
} from "@/hooks/useTelemetry";
import type { TelemetryDataPoint } from "@/types";

/**
 * Progressive chart resolution: for any viewport below the full-view
 * fraction of the domain, fetch a higher-resolution window (debounced) and
 * merge it into the base series. resolveDetailWindow's stale-window branch always returns a
 * window that covers the viewport, so each refetch fully replaces base-res
 * data in view. Each distinct window is a distinct react-query key, so
 * repeated refetches as the user pans are cache-friendly.
 */
export function useProgressiveDetailWindow(
  sessionId: string,
  viewport: TimeWindow | null | undefined,
  fullRange: TimelineRange | null,
  baseData: TelemetryDataPoint[],
  channels?: string[],
): {
  chartData: TelemetryDataPoint[];
  resetDetail: () => void;
} {
  // High-res detail window for the zoomed-in chart.
  const [detail, setDetail] = useState<{
    window: TimeWindow;
    filter: {
      startTime: number;
      endTime: number;
      maxPoints: number;
      channels?: string[];
    };
  } | null>(null);

  useEffect(() => {
    const full = fullRange;
    if (!viewport || !full) {
      setDetail(null);
      return;
    }
    const fullDomain = {
      startTime: full.start,
      endTime: full.end,
    };
    const timer = setTimeout(() => {
      const nextChannels =
        channels && channels.length > 0 ? channels : undefined;
      setDetail((prev) => {
        const next = resolveDetailWindow(
          viewport,
          fullDomain,
          prev?.window ?? null,
        );
        if (!next) return null;
        const channelsUnchanged =
          prev?.filter.channels === nextChannels ||
          (prev?.filter.channels?.length === nextChannels?.length &&
            (prev?.filter.channels ?? []).every(
              (c, i) => c === nextChannels?.[i],
            ));
        if (
          prev &&
          prev.window.startTime === next.startTime &&
          prev.window.endTime === next.endTime &&
          channelsUnchanged
        ) {
          return prev;
        }
        return {
          window: next,
          filter: {
            startTime: next.startTime,
            endTime: next.endTime,
            maxPoints: TELEMETRY_CHART_MAX_POINTS,
            channels: nextChannels,
          },
        };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [viewport, fullRange, channels]);

  const detailQuery = useTelemetryData(
    sessionId,
    detail?.filter,
    !!detail,
  );

  const chartData = useMemo(() => {
    const detailRows = detailQuery.data?.result?.data;
    if (!detail || !detailRows) return baseData;
    return mergeTelemetryData(baseData, detailRows, detail.window);
  }, [baseData, detail, detailQuery.data]);

  // Exposed so the page's per-session reset block can clear the detail
  // window when the session changes. Stable identity; safe to call during
  // render (it only forwards to setState).
  const resetDetail = useCallback(() => setDetail(null), []);

  return { chartData, resetDetail };
}
